import { useEffect, useMemo, useState } from "react";
import { Option } from "../../common/commonSelect";
import webservice_url, {
  master_baseUrl,
} from "../../webservice/Webservice_url";
import useApi from "../useApi";

type DataTypes = {
  location_name: string;
  location_id: string;
};

// ========= Admin PORTAL ===========

// Location by multiple Project IDs
export function useLocationByProjectId(
  projectIds: string[],
  selectedLocationIds?: string[],
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading, error } = useApi<any>("", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  // Convert API data into dropdown options
  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.location_name ?? "",
      value: d?.location_name ?? "",
    }));
  }, [dataList]);

  // Fetch locations for selected projects
  const fetchData = async () => {
    // No project selected
    if (!Array.isArray(projectIds) || projectIds.length === 0) {
      setDataList([]);
      return;
    }

    try {
      const payload = {
        projectIds: projectIds,
      };

      // Single POST request
      const res = await callApi(
        payload,
        webservice_url.get_locations,
      );

      // API response is { data: [...] }
      const data: DataTypes[] = Array.isArray(res?.data)
        ? res.data
        : [];

      // Remove duplicate locations using location_name
      const uniqueData: DataTypes[] = Array.from(
        new Map<string, DataTypes>(
          data.map((item) => [
            item.location_name,
            item,
          ]),
        ).values(),
      );

      setDataList(uniqueData);
    } catch (err: any) {
      console.error("Error fetching locations:", err);
      setDataList([]);
    }
  };

  // Fetch whenever selected projects change
  useEffect(() => {
    if (
      !Array.isArray(projectIds) ||
      projectIds.length === 0
    ) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(projectIds)]);

  // Selected location items
  const selectedItems = useMemo<DataTypes[]>(() => {
    if (
      !Array.isArray(selectedLocationIds) ||
      selectedLocationIds.length === 0
    ) {
      return [];
    }

    return dataList.filter((item) =>
      selectedLocationIds.includes(item.location_name),
    );
  }, [dataList, selectedLocationIds]);

  return {
    dataList,
    options,
    loading,
    error,
    selectedItems,
    fetchData,
  };
}

// Location by managerId
export function useLocationsByManagerId(managerId: string, projectId: string) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading } = useApi<any>(
    webservice_url.get_locations_by_managerId,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.location_name ?? "",
      value: d?.location_name ?? "",
    }));
  }, [dataList]);

  // Fetch data
  const fetchData = async () => {
    try {
      const res = await callApi({
        manager_id: managerId ?? "",
        project_id: projectId ?? "",
      });

      setDataList(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err: any) {
      console.error(err);
      setDataList([]);
    }
  };

  useEffect(() => {
    if (!managerId || !projectId) {
      return;
    }

    fetchData();
  }, [managerId, projectId]);

  return {
    dataList,
    options,
    loading,
  };
}

// ========= END Admin PORTAL ===========

// ========= VERIFIER PORTAL ===========

export function useLocationByProjectId_Verifier(
  projectId: string,
  selectedLocationId?: string,
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  const { callApi, loading } = useApi("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchData = async () => {
    if (!projectId) {
      return;
    }

    try {
      const res = await callApi(
        undefined,
        `${webservice_url.fetchLocation}?project_id=${projectId}`,
      );

      setDataList(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err) {
      setDataList([]);
      console.error(err);
    }
  };

  const options = useMemo<Option[]>(
    () =>
      dataList.map((d) => ({
        label: d.location_name,
        value: d.location_name,
      })),
    [dataList],
  );

  const selectedItem = useMemo(
    () => dataList.find((d) => d.location_id === selectedLocationId) ?? null,
    [dataList, selectedLocationId],
  );

  return {
    dataList,
    options,
    loading,
    selectedItem,
    fetchData,
  };
}

// ========= END VERIFIER PORTAL ===========

// Get location by multiple project IDs
export function useLocationByMultiProjectId(
  projectId: string[],
  selectedLocationId?: string,
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading, error } = useApi<any>(
    webservice_url.getLocationByMultiProject,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.location_name ?? "",
      value: d?.location_name ?? "",
    }));
  }, [dataList]);

  // Fetch data
  const fetchData = async () => {
    if (!Array.isArray(projectId) || projectId.length === 0) {
      setDataList([]);
      return;
    }

    try {
      const res = await callApi({
        projectIds: projectId,
      });

      setDataList(Array.isArray(res) ? res : (res?.data ?? []));
    } catch (err: any) {
      setDataList([]);
      console.error(err);
    }
  };

  useEffect(() => {
    if (!Array.isArray(projectId) || projectId.length === 0) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(projectId)]);

  // Selected item
  const selectedItem = useMemo<any>(() => {
    if (!selectedLocationId) {
      return null;
    }

    return dataList.find((d) => d?.location_id === selectedLocationId) ?? null;
  }, [dataList, selectedLocationId]);

  return {
    dataList,
    options,
    loading,
    error,
    selectedItem,
    fetchData,
  };
}
